/**
 * Hook for fetching research associated with a entity (contact or company).
 */

import { useEffect, useMemo, useRef } from 'react';

import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import { useCrmObjectList } from '../../store/hooks';

/**
 * Research status values.
 */
export type ResearchStatus = 'queued' | 'in_progress' | 'complete' | 'failed';

/**
 * Research with helper properties extracted.
 */
export interface Research {
  entity_id: string;
  status: ResearchStatus;
  summary: string | null;
  view_definition_json: string | null;
  error_message: string | null;
  created_at: string | null;
  completed_at: string | null;
  last_event_seq: number;
}

/**
 * Extract research data from CRM object.
 */
export function extractResearchData(obj: CrmObject): Research {
  const props = obj.properties || {};

  return {
    entity_id: obj.entity_id,
    status:
      (getPropertyValue<string>(props.ahoy_status) as ResearchStatus) ||
      'queued',
    summary: getPropertyValue<string>(props.ahoy_summary) || null,
    view_definition_json:
      getPropertyValue<string>(props.ahoy_view_definition) || null,
    error_message: getPropertyValue<string>(props.ahoy_error_message) || null,
    created_at: getPropertyValue<string>(props.ahoy_created_at) || null,
    completed_at: getPropertyValue<string>(props.ahoy_completed_at) || null,
    last_event_seq: obj.last_event_seq,
  };
}

interface UseResearchOptions {
  /** Organization ID */
  orgId: number;
  /** Entity ID to get research for */
  entityId: string;
  /** Record type: 'contact' or 'company' */
  recordType: 'contact' | 'company';
  /** Whether to enable the query (default: true) */
  enabled?: boolean;
}

/** Polling interval when research is in progress (3 seconds) */
export const POLLING_INTERVAL_MS = 3000;

export function useResearch({
  orgId,
  entityId,
  recordType: _recordType,
  enabled = true,
}: UseResearchOptions) {
  const isEnabled = enabled && !!orgId && !!entityId;

  // Fetch research via the store-aware list hook (handles pending creations,
  // optimistic deletion, and WS-driven property edits internally)
  const listResult = useCrmObjectList(
    orgId,
    'ahoy_research',
    {
      associated_with_entity_id: entityId,
      properties: [
        'ahoy_status',
        'ahoy_summary',
        'ahoy_view_definition',
        'ahoy_error_message',
        'ahoy_created_at',
        'ahoy_completed_at',
      ],
      sort: 'ahoy_created_at',
      order: 'desc',
    },
    { enabled: isEnabled }
  );

  // Map raw CRM objects through extractResearchData
  const research = useMemo(
    () => (listResult.data?.results ?? []).map(extractResearchData),
    [listResult.data?.results]
  );

  // Compute whether any research is in progress (for UI indicators)
  const hasInProgressResearch = research.some(
    r => r.status === 'in_progress' || r.status === 'queued'
  );

  // Keep a stable ref to refetch so the interval callback doesn't need it as a dep
  const refetchRef = useRef(listResult.refetch);
  refetchRef.current = listResult.refetch;

  // Polling: refetch every POLLING_INTERVAL_MS when research is pending.
  // We skip the refetch when the tab is hidden or the browser is offline so
  // this manual setInterval matches React Query's default refetchInterval
  // behavior (which pauses on hidden tabs / no network) — without this,
  // background tabs would keep firing requests indefinitely.
  useEffect(() => {
    if (!isEnabled || !hasInProgressResearch) {
      return;
    }
    const id = setInterval(() => {
      if (document.visibilityState === 'visible' && navigator.onLine) {
        void refetchRef.current();
      }
    }, POLLING_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isEnabled, hasInProgressResearch]);

  return {
    research,
    isLoading: listResult.isLoading,
    error: listResult.error,
    refetch: listResult.refetch,
    hasInProgressResearch,
  };
}
